import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

interface ApiSettings {
  twilio: {
    sid: string;
    token: string;
    phoneNumber: string;
    verified: boolean;
  };
  sendgrid: {
    key: string;
    verified: boolean;
  };
  hospital: {
    endpoint: string;
    key: string;
    verified: boolean;
  };
}

// Simple encryption/decryption (in production, use proper encryption)
const encrypt = (text: string): string => {
  return btoa(text); // Base64 encoding (replace with proper encryption)
};

const decrypt = (encryptedText: string): string => {
  try {
    return atob(encryptedText); // Base64 decoding
  } catch {
    return '';
  }
};

export const useSecureSettings = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState<ApiSettings>({
    twilio: { sid: '', token: '', phoneNumber: '', verified: false },
    sendgrid: { key: '', verified: false },
    hospital: { endpoint: 'https://hospital-api.com/v1', key: '', verified: false }
  });
  const [loading, setLoading] = useState(true);

  // Load settings from Supabase
  useEffect(() => {
    if (user) {
      loadSettings();
    }
  }, [user]);

  const loadSettings = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('user_settings')
        .select('*')
        .eq('user_id', user?.id)
        .eq('setting_type', 'api_credentials')
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading settings:', error);
        return;
      }

      if (data?.encrypted_value) {
        try {
          const decryptedSettings = JSON.parse(decrypt(data.encrypted_value));
          setSettings(decryptedSettings);
        } catch (error) {
          console.error('Error decrypting settings:', error);
        }
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (newSettings: ApiSettings) => {
    if (!user) return false;

    try {
      const encryptedValue = encrypt(JSON.stringify(newSettings));
      
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          setting_type: 'api_credentials',
          encrypted_value: encryptedValue,
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'user_id,setting_type'
        });

      if (error) {
        console.error('Error saving settings:', error);
        return false;
      }

      setSettings(newSettings);
      return true;
    } catch (error) {
      console.error('Error saving settings:', error);
      return false;
    }
  };

  const updateSettings = (updates: Partial<ApiSettings>) => {
    const newSettings = { ...settings, ...updates };
    setSettings(newSettings);
    return saveSettings(newSettings);
  };

  const clearSettings = async () => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('user_settings')
        .delete()
        .eq('user_id', user.id)
        .eq('setting_type', 'api_credentials');

      if (error) {
        console.error('Error clearing settings:', error);
        return false;
      }

      setSettings({
        twilio: { sid: '', token: '', phoneNumber: '', verified: false },
        sendgrid: { key: '', verified: false },
        hospital: { endpoint: 'https://hospital-api.com/v1', key: '', verified: false }
      });

      return true;
    } catch (error) {
      console.error('Error clearing settings:', error);
      return false;
    }
  };

  return {
    settings,
    loading,
    updateSettings,
    saveSettings,
    clearSettings
  };
};